import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GoogleMap from 'react-google-maps/lib/components/GoogleMap';
import moment from 'moment';

import { Set, is } from 'immutable';
import { useRedux } from '../../../states/redux-state';
import { selectDevices } from '../../../stores/reducers/devicesData/selector';
import { tagShowToggle, hideAllTags } from '../../../stores/reducers/tagsDevicesMap/AC';
import { ALL_DEVICES_TAG } from '../../../shared/helpers';
import { iFullStoreState } from '../../../shared/interfaces';

const ONE_DAY = 86_400_000; // 24 hours in milliseconds

export const useTagMap = (filter: boolean) => {
  const dispatch = useDispatch();

  const devices = useSelector(selectDevices);
  const devicesIds = useMemo(() => [...devices.keys()], [devices]);

  const mapRef = useSelector<
    iFullStoreState,
    React.RefObject<GoogleMap> | null
  >((state) => state.gmap.mapRef());

  const devicesLastPing = useRedux((s) => s.devicesData.devicesLastPing)
    .valueSeq()
    .toArray()
    .filter((it) => it !== null && moment().diff(it.time) < ONE_DAY)
    .map((it) => it.device);

  const devicesMarker = useRedux((s) => s.devicesData.devicesLastPing)
    .valueSeq()
    .toArray()
    .filter((it) => it !== null);

  const showedTagsIds = useSelector<iFullStoreState, Set<string>>(
    (state) => state.tagsDevicesMap.showedTagsIds,
    (left, right) => is(left, right),
  );

  const toggleTag = useCallback(
    (tagId: string) => {
      const isAlreadyVisible = showedTagsIds.has(tagId);
      const isAllDevicesModeActive = showedTagsIds.has(ALL_DEVICES_TAG);

      if (filter) {
        dispatch(tagShowToggle(tagId, devicesLastPing, filter));
      } else if (tagId === ALL_DEVICES_TAG) {
        if (isAlreadyVisible) {
          dispatch(hideAllTags());
        } else {
          dispatch(hideAllTags()); // clear any previously shown specific tags
          dispatch(tagShowToggle(tagId, devicesIds));
        }
      } else {
        if (!isAlreadyVisible) {
          const markerBounds = new google.maps.LatLngBounds();

          devicesMarker.forEach((marker) => {
            markerBounds.extend(
              new google.maps.LatLng(
                marker.coordinates.location.lat,
                marker.coordinates.location.lng,
              ),
            );
          });

          mapRef?.current?.fitBounds(markerBounds);
        }

        if (isAllDevicesModeActive) {
          dispatch(hideAllTags());
          dispatch(tagShowToggle(tagId));
        } else {
          dispatch(tagShowToggle(tagId));
        }
      }
    },
    [dispatch, filter, devicesLastPing, devicesIds, devicesMarker, mapRef, showedTagsIds],
  );

  return { toggleTag, devicesIds };
};


